import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const PageAlertsModal = ({ pageId, pageUrl, isOpen, onClose }) => {
  const { currentLang, fetchWithAuth, API_URL } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [groupedAlerts, setGroupedAlerts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [showTabDropdown, setShowTabDropdown] = useState(false);

  useEffect(() => {
    if (isOpen && pageId) {
      fetchAlerts();
    }
  }, [isOpen, pageId]);

   // Управляем прокруткой body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/pages/${pageId}/alerts`);
      const data = await response.json();

      if (response.ok && data.success) {
        setAlerts(data.data.alerts || []);
        setGroupedAlerts(data.data.grouped_alerts || {});
        setTotalCount(data.data.total_count || 0);
      } else {
        setError(data.message || 'Ошибка загрузки алертов');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError('Ошибка сети при загрузке алертов');
      }
      
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return '🔴';
      case 'warning':
        return '🟡';
      case 'info':
        return '🔵';
      default:
        return '⚪';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      metadata: '📄',
      headings: '📝',
      links: '🔗',
      images: '🖼️',
      performance: '⚡',
      accessibility: '♿',
      seo: '🔍',
      security: '🔒',
      content: '📝',
      technical: '⚙️'
    };
    return icons[category] || '📋';
  };

  const getCategoryName = (category) => {
    const names = {
      metadata: currentLang === 'ru' ? 'Метаданные' : 'Metadata',
      headings: currentLang === 'ru' ? 'Заголовки' : 'Headings',
      links: currentLang === 'ru' ? 'Ссылки' : 'Links',
      images: currentLang === 'ru' ? 'Изображения' : 'Images',
      performance: currentLang === 'ru' ? 'Производительность' : 'Performance',
      accessibility: currentLang === 'ru' ? 'Доступность' : 'Accessibility',
      seo: 'SEO',
      security: currentLang === 'ru' ? 'Безопасность' : 'Security',
      content: currentLang === 'ru' ? 'Контент' : 'Content',
      technical: currentLang === 'ru' ? 'Технические' : 'Technical'
    };
    return names[category] || category;
  };

  const getSeverityName = (severity) => {
    const names = {
      critical: currentLang === 'ru' ? 'Критично' : 'Critical',
      warning: currentLang === 'ru' ? 'Предупреждение' : 'Warning',
      info: currentLang === 'ru' ? 'Информация' : 'Info'
    };
    return names[severity] || severity;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(
      currentLang === 'ru' ? 'ru-RU' : 'en-US',
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    );
  };

  const filteredAlerts = alerts.filter(alert => {
    if (activeTab === 'all') return true;
    if (activeTab === 'critical') return alert.severity === 'critical';
    if (activeTab === 'warning') return alert.severity === 'warning';
    if (activeTab === 'info') return alert.severity === 'info';
    return alert.category === activeTab;
  }).sort((a, b) => {
    // Сортируем по важности: critical > warning > info
    const severityOrder = { critical: 3, warning: 2, info: 1 };
    const aOrder = severityOrder[a.severity] || 0;
    const bOrder = severityOrder[b.severity] || 0;
    
    if (aOrder !== bOrder) {
      return bOrder - aOrder; // Сначала критические
    }
    
    // Если важность одинаковая, сортируем по дате создания (новые сначала)
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const getTabCount = (tab) => {
    if (tab === 'all') return totalCount;
    if (['critical', 'warning', 'info'].includes(tab)) {
      return alerts.filter(alert => alert.severity === tab).length;
    }
    return groupedAlerts[tab]?.length || 0;
  };

  const tabs = [
    { key: 'all', name: currentLang === 'ru' ? 'Все' : 'All' },
    { key: 'critical', name: currentLang === 'ru' ? 'Критично' : 'Critical' },
    { key: 'warning', name: currentLang === 'ru' ? 'Предупреждения' : 'Warnings' },
    { key: 'info', name: currentLang === 'ru' ? 'Информация' : 'Info' },
    ...Object.keys(groupedAlerts).map(category => ({
      key: category,
      name: getCategoryName(category)
    }))
  ];

  const currentTab = tabs.find(tab => tab.key === activeTab);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[95vh] flex flex-col">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-3 sm:p-4 lg:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg flex-shrink-0">
              <svg className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 truncate">
                {currentLang === 'ru' ? 'Алерты страницы' : 'Page Alerts'}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 break-all line-clamp-2">
                {pageUrl}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-2"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Вкладки - мобильная версия с выпадающим списком */}
        <div className="border-b border-gray-200 flex-shrink-0">
          {/* Мобильная версия */}
          <div className="block sm:hidden">
            <div className="relative">
              <button
                onClick={() => setShowTabDropdown(!showTabDropdown)}
                className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 bg-white border-b border-gray-200"
              >
                <span className="flex items-center space-x-2">
                  <span>{currentTab?.name}</span>
                  <span className="px-2 py-1 text-xs rounded-full bg-red-200 text-red-800">
                    {getTabCount(activeTab)}
                  </span>
                </span>
                <svg className={`w-4 h-4 transition-transform ${showTabDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showTabDropdown && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setActiveTab(tab.key);
                        setShowTabDropdown(false);
                      }}
                      className={`w-full flex items-center justify-between p-3 text-sm hover:bg-gray-50 transition-colors ${
                        activeTab === tab.key ? 'bg-red-50 text-red-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="flex items-center space-x-2">
                        <span>{tab.name}</span>
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        activeTab === tab.key
                          ? 'bg-red-200 text-red-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {getTabCount(tab.key)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Десктопная версия */}
          <div className="hidden sm:flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
                  activeTab === tab.key
                    ? 'border-red-500 text-red-600 bg-red-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{tab.name}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  activeTab === tab.key
                    ? 'bg-red-200 text-red-800'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {getTabCount(tab.key)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Содержимое с прокруткой */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-red-500"></div>
                <span className="text-sm sm:text-base text-gray-600">
                  {currentLang === 'ru' ? 'Загрузка алертов...' : 'Loading alerts...'}
                </span>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 sm:p-6">
              <div className="bg-red-100 border border-red-400 text-red-700 rounded-lg p-3 sm:p-4 text-sm">
                {error}
              </div>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <div className="text-center px-4">
                <div className="p-2 sm:p-3 bg-green-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  {currentLang === 'ru' ? 'Алерты не найдены' : 'No alerts found'}
                </h3>
                <p className="text-sm text-gray-600">
                  {currentLang === 'ru' 
                    ? 'Для выбранных критериев алерты не обнаружены.' 
                    : 'No alerts found for the selected criteria.'
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4">
              {filteredAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`border rounded-lg p-3 sm:p-4 ${getSeverityColor(alert.severity)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <span className="text-xl sm:text-2xl">{alert.icon || getSeverityIcon(alert.severity)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0 mb-2">
                          <h4 className="text-sm font-semibold text-gray-900 break-words">
                            {alert.alert_title}
                          </h4>
                          <div className="flex flex-wrap gap-1 sm:gap-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(alert.severity)}`}>
                              {getSeverityName(alert.severity)}
                            </span>
                            <span className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                              <span>{getCategoryIcon(alert.category)}</span>
                              <span className="hidden sm:inline">{getCategoryName(alert.category)}</span>
                            </span>
                          </div>
                        </div>
                        
                        {alert.description && (
                          <div className="mb-2 sm:mb-3">
                            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed break-words">
                              {alert.description}
                            </p>
                          </div>
                        )}
                        
                        {alert.details && (
                          <div className="mb-2 sm:mb-3">
                            <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap break-words">
                              {alert.details}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 text-xs text-gray-500">
                          <span className="break-all">
                            {currentLang === 'ru' ? 'ID:' : 'ID:'} {alert.alert_key}
                          </span>
                          <span>
                            {currentLang === 'ru' ? 'Создан:' : 'Created:'} {formatDate(alert.created_at)}
                          </span>
                          {alert.updated_at !== alert.created_at && (
                            <span>
                              {currentLang === 'ru' ? 'Обновлен:' : 'Updated:'} {formatDate(alert.updated_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                      <span className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
                        alert.severity === 'critical' ? 'bg-red-500' :
                        alert.severity === 'warning' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      }`}></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Футер */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 lg:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0 space-y-3 sm:space-y-0">
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
            {currentLang === 'ru' 
              ? `Показано ${filteredAlerts.length} из ${totalCount} алертов`
              : `Showing ${filteredAlerts.length} of ${totalCount} alerts`
            }
          </div>
          <div className="flex items-center justify-center sm:justify-end space-x-2 sm:space-x-4">
            <button
              onClick={fetchAlerts}
              disabled={loading}
              className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
              ) : (
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              <span>{currentLang === 'ru' ? 'Обновить' : 'Refresh'}</span>
            </button>
            <button
              onClick={onClose}
              className="px-3 sm:px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              {currentLang === 'ru' ? 'Закрыть' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageAlertsModal; 